import { z } from "zod"

const point = z.tuple([z.number().finite(), z.number().finite(), z.number().finite()])

/** Point de vue du rendu realiste : celui de la vue 3D (metres, axe Y vers le haut), ou null pour la vue automatique */
export const renderCameraSchema = z
  .object({
    position: point,
    target: point,
    /** Angle de champ vertical, en degres */
    fov: z.number().min(10).max(120),
  })
  .nullable()

/** Orientation du projet pour le rendu : le nord (degres, sens horaire depuis le haut du plan), ou null sans orientation */
export const renderOrientationSchema = z
  .object({
    northDeg: z.number().finite(),
  })
  .nullable()

/** Qualite du rendu : standard (1280 x 720, une minute environ) ou haute (1920 x 1080, brins d'herbe en relief, environ cinq minutes) */
export const renderQualitySchema = z.enum(["standard", "haute"]).default("standard")
export type RenderQuality = z.infer<typeof renderQualitySchema>

/** Calcul du rendu : serveur (processeur du serveur, gratuit) ou gpu (carte graphique chez Modal, admins seulement — GPU) */
export const renderEngineSchema = z.enum(["serveur", "gpu"]).default("serveur")
export type RenderEngine = z.infer<typeof renderEngineSchema>

/** Moteur de la finition IA du decor (FIA) : la maison reste l'image de Blender, l'IA repeint le jardin et le ciel */
export const renderAiModelSchema = z.enum(["sdxl", "qwen", "sd35", "flux_schnell"])
export type RenderAiModel = z.infer<typeof renderAiModelSchema>

/** La finition qu'une demande peut porter : seul Qwen est garde apres l'essai (decision de Boris du 2026-11-02, FIA) */
export const renderFinishRequestSchema = z.enum(["qwen"]).nullable().default(null)

/** La reponse a une demande de rendu : son rang dans la file du serveur (null sur la carte graphique) et son calcul */
export interface RenderRequestOutcome {
  id: string
  position: number | null
  engine: RenderEngine
}

/** Etat d'une demande de rendu dans la file */
export const renderStatusSchema = z.enum(["en_attente", "en_cours", "termine", "echoue"])
export type RenderStatus = z.infer<typeof renderStatusSchema>

/** Resultat renvoye par le service de rendu : l'image (JPEG en base64), avec les passes de la finition IA (zip en
 *  base64) quand la demande en porte une, ou l'echec */
export const renderResultSchema = z.union([
  z.object({ image_base64: z.string().min(1), passes_base64: z.string().min(1).optional() }),
  z.object({ error: z.string().min(1).max(2000) }),
])
export type RenderResult = z.infer<typeof renderResultSchema>

/** Resultat de la finition IA renvoye par Modal : l'image finie (JPEG en base64) ou l'echec */
export const renderFinitionResultSchema = z.union([
  z.object({ image_base64: z.string().min(1) }),
  z.object({ error: z.string().min(1).max(2000) }),
])
export type RenderFinitionResult = z.infer<typeof renderFinitionResultSchema>

/** Taille maximale de la maquette envoyee au rendu */
export const RENDER_MAX_GLB_BYTES = 20 * 1024 * 1024

/** Une demande de rendu telle que la fenetre la suit (sans maquette ni image) */
export interface RenderJobView {
  id: string
  status: RenderStatus
  quality: RenderQuality
  /** Calcul prevu ou en cours */
  engine: RenderEngine
  /** Demandee sur la carte graphique, calculee sur le serveur */
  gpuFallback: boolean
  /** Finition IA du decor (FIA), ou null */
  aiModel: RenderAiModel | null
  /** Finition IA demandee, en echec : l'image est celle de Blender */
  aiFallback: boolean
  /** En cours de finition IA : Blender a fini */
  stage?: "finition"
  /** Rang dans la file du serveur (1 : la prochaine), pour une demande en attente sur le serveur */
  position?: number
  createdAt: string
  /** Debut du calcul (prise par le serveur ou la carte graphique) */
  startedAt?: string
  finishedAt?: string
  userName?: string
  /** Demandee par l'utilisateur qui lit la liste */
  mine: boolean
  /** Pret, et pas encore vu par son auteur (qui lit la liste) */
  isNew: boolean
  /** L'utilisateur qui lit la liste peut le supprimer (son auteur, ou le proprietaire du projet) */
  canDelete: boolean
  error?: string
}

/** Evenement publie par Mercure a l'auteur d'une demande, a chaque changement d'etat */
export interface RenderEvent {
  id: string
  projectId: string
  /** supprime : la demande a ete supprimee (par son auteur ou le proprietaire du projet) */
  status: RenderStatus | "supprime"
  /** Calcul prevu ou en cours, et la bascule de la carte graphique vers le serveur */
  engine?: RenderEngine
  gpuFallback?: boolean
  /** Rang dans la file du serveur, pour une demande en attente sur le serveur */
  position?: number
  /** Message d'echec, pour une demande echouee */
  error?: string
  /** La finition IA (FIA) : le moteur, son echec (image de Blender), et l'etape en cours (finition : Blender a fini) */
  aiModel?: RenderAiModel | null
  aiFallback?: boolean
  stage?: "finition"
}
